import { useCallback, type KeyboardEvent } from 'react';

import {
  Box,
  type StackProps,
  HStack,
  RotateClockwiseIcon,
  RotateCounterClockwiseIcon,
  Tooltip,
} from '@frontend/legacy-components';

import {
  DXF_Z_INDEX,
  ROTATE_CHIP_BG,
  ROTATE_CHIP_BG_HOVER,
} from '../constants';

/**
 * `role="button"` on a `div` rather than a real `<button>`, matching `ViewerButton`:
 * the global `button { background: none }` in reset.scss would otherwise strip the
 * chip's background.
 */
const ROTATE_BUTTON_STYLE = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '32px',
  width: '32px',
  borderRadius: '2px',
  cursor: 'pointer',
  color: 'white',
  bg: ROTATE_CHIP_BG,
  _hover: { bg: ROTATE_CHIP_BG_HOVER },
  _focusVisible: {
    outline: '2px solid',
    outlineColor: 'interaction.focus',
    outlineOffset: '2px',
  },
} as const;

export type DxfRotateControlsProps = {
  onRotateClockwise: () => void;
  onRotateCounterClockwise: () => void;
} & StackProps;

/**
 * Bottom-left rotate pair, matching the CAD viewer's 2D controls.
 *
 * Pure and controlled: placement, size and the dark translucent chip are
 * deliberately identical to `DxfRotateButton` in `partviewer`'s
 * `renderer-component`, so a user moving between the legacy DXF path and this
 * one finds the same control in the same place. Rotation itself is a viewport
 * operation owned by the session — `useDxfRotateControls` is the piece that
 * wires the clicks.
 */
export const DxfRotateControls = ({
  onRotateClockwise,
  onRotateCounterClockwise,
  ...props
}: DxfRotateControlsProps) => {
  const handleCounterClockwiseKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onRotateCounterClockwise();
      }
    },
    [onRotateCounterClockwise],
  );

  const handleClockwiseKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onRotateClockwise();
      }
    },
    [onRotateClockwise],
  );

  return (
    <HStack
      position="absolute"
      bottom={2}
      left={2}
      zIndex={DXF_Z_INDEX.CHROME}
      spacing={0.5}
      userSelect="none"
      {...props}
    >
      <Tooltip placement="top" label="Rotate counterclockwise">
        <Box
          as="div"
          role="button"
          tabIndex={0}
          aria-label="Rotate counterclockwise"
          onClick={onRotateCounterClockwise}
          onKeyDown={handleCounterClockwiseKeyDown}
          {...ROTATE_BUTTON_STYLE}
        >
          <RotateCounterClockwiseIcon boxSize={2.5} />
        </Box>
      </Tooltip>
      <Tooltip placement="top" label="Rotate clockwise">
        <Box
          as="div"
          role="button"
          tabIndex={0}
          aria-label="Rotate clockwise"
          onClick={onRotateClockwise}
          onKeyDown={handleClockwiseKeyDown}
          {...ROTATE_BUTTON_STYLE}
        >
          <RotateClockwiseIcon boxSize={2.5} />
        </Box>
      </Tooltip>
    </HStack>
  );
};
